import type { OutputLine } from "@skaile/workspaces";
import pc from "picocolors";
import type { TelemetryLogEntry } from "./telemetry.js";

/**
 * Render a single OutputLine to an ANSI-colored string for terminal display.
 *
 * Each `OutputLine` variant is mapped to a distinct colour: user input (cyan/bold),
 * agent text (plain), tool calls (blue italic), questions (yellow), errors (red),
 * status/system messages (dim italic), subagent lifecycle (blue/green italic), and
 * separator lines (dim dashes). Returns an empty string for unrecognised variants.
 *
 * @param line - The typed output line to render.
 * @returns ANSI-coloured string ready for terminal output.
 *
 * @docLink packages/tui/concepts#repl-renderer
 */
export function renderLine(line: OutputLine): string {
  switch (line.type) {
    case "user":
      return pc.cyan(pc.bold(`> ${line.content}`));
    case "text":
      return line.content;
    case "tool":
      return pc.blue(pc.italic(`  Running: \`${line.name}\`...`));
    case "question":
      return pc.yellow(`?? ${line.text}\n   Reply below.`);
    case "error":
      return pc.red(`Error: ${line.message}`);
    case "status":
      return pc.dim(pc.italic(`${line.phase}...`));
    case "system":
      return pc.dim(pc.italic(line.message));
    case "subagent": {
      const icon = line.status === "started" ? "▶" : "✓";
      const color = line.status === "started" ? pc.blue : pc.green;
      return color(pc.italic(`  ${icon} ${line.name}: ${line.description}`));
    }
    case "separator":
      return pc.dim("─".repeat(60));
    default:
      return "";
  }
}

/**
 * Render a telemetry log entry to an ANSI string, or null to suppress.
 *
 * Formats `generation` entries with model name, token counts, cost, and duration;
 * `span_end` entries for `tool_call` and `agent_turn` kinds; and `trace_start` /
 * `trace_end` lifecycle entries. All output is styled dim for visual de-emphasis.
 * Returns `null` for entry kinds that should not appear in the log panel.
 *
 * @param entry - The structured telemetry event to render.
 * @returns Dim ANSI string, or `null` if the entry should be suppressed.
 *
 * @docLink packages/tui/concepts#repl-renderer
 */
export function renderLog(entry: TelemetryLogEntry): string | null {
  switch (entry.kind) {
    case "generation": {
      const tokens = `${entry.inputTokens}↑ ${entry.outputTokens}↓`;
      const cost = entry.costUsd > 0 ? ` $${entry.costUsd.toFixed(4)}` : "";
      const dur = entry.durationMs > 0 ? ` ${(entry.durationMs / 1000).toFixed(1)}s` : "";
      return pc.dim(`[gen] ${entry.model} ${tokens}${cost}${dur}`);
    }
    case "span_end":
      if (entry.spanKind === "tool_call") {
        return pc.dim(`[tool] ${entry.name} ${entry.status} ${entry.durationMs}ms`);
      }
      if (entry.spanKind === "agent_turn") {
        return pc.dim(`[turn] ${entry.name} ${(entry.durationMs / 1000).toFixed(1)}s`);
      }
      return null;
    case "trace_start":
      return pc.dim(`[trace] ${entry.name} started`);
    case "trace_end":
      return pc.dim(`[trace] done ${(entry.durationMs / 1000).toFixed(1)}s`);
    default:
      return null;
  }
}
